import ApiState from "../../enums/APISTATE";
import moment from 'moment';
import { generateUUID } from "../../util/uuid";
import Vue from "vue";
import { isActive, compare, compareEmployeeOrderNumber, compareOrderNumber } from "../../util/statisticsComparers";
import Repository from '@/repositories/RepositoryFactory';

const allocationsRepository = Repository.get('allocations');
const groupTemplateRepository = Repository.get('groupTemplateRepository');

const allocationForDay = (day, employeeId, allocationsTimeOff) => {
  return allocationsTimeOff
    .filter(o => o.employeeId === employeeId)
    .filter(o => moment(o.startDate).format('YYYY-MM-DD') === day || 
      (o.allocationSpanInDays > 1 && moment(o.startDate).format('YYYY-MM-DD') <= day && day <= moment(o.endDateInclusive).format('YYYY-MM-DD')))
};

const isAllocatedForDay = (day, employeeId, allocationsTimeOff) => {
  return allocationsTimeOff
    .filter(o => o.employeeId === employeeId)
    .filter(o => o.replacementId && o.circleId)
    .filter(o => moment(o.startDate).format('YYYY-MM-DD') === day || 
      (o.allocationSpanInDays > 1 && moment(o.startDate).format('YYYY-MM-DD') <= day && day <= moment(o.endDateInclusive).format('YYYY-MM-DD')))
};

const dayBetween = (start, end) => {
  const a = moment(start);
  const b = moment(end);
  const totalDays = b.diff(a, 'days');
  return totalDays;
};


export default {
  namespaced: true,
  state: {
    apiState: ApiState.INIT,
    allocations: [],
    employeeStatistics: [],
    formGroupTemplate: null,
    activeSidebar: '',
    isNewGroupTemplate: false,
    loadStatisticsStartDate: moment().month(0).date(1),
    loadStatisticsEndDate: moment().month(11).date(31),
    loadStatisticsGroupFilter: '',
    loadStatisticsRegionFilter: '',

  },
  mutations: {
    setStatisticsApiState(state, apiState) {
      state.apiState = apiState;
    },
    clearEmployeeStatistics(state) {
      state.employeeStatistics = [];
    },
    setEmployeeStatistics(state, employeeStatistics) {
      state.employeeStatistics = employeeStatistics;
    },
    pushToAllocations(state, entry) {
      state.allocations.push(entry);
    },
    setAllocations(state, allocations) {
      state.allocations = allocations;
    },
    filterAllocations(state, element) {
      state.allocations = state.allocations.filter(element);
    },
    clearAllocations(state) {
      state.allocations = [];
    },
    pushToEmployeeStatistics(state, entry) {
      state.employeeStatistics.push(entry);
    },
    setActiveSidebar(state, activeSidebar) {
      state.activeSidebar = activeSidebar;
    },
    setFormGroupTemplate(state, formGroupTemplate) {
      Vue.set(state, "formGroupTemplate", formGroupTemplate);
      if (formGroupTemplate) {
        Vue.set(state.formGroupTemplate, "fromDate", moment(formGroupTemplate.fromDate).format('YYYY-MM-DD'));
        Vue.set(state.formGroupTemplate, "toDate", moment(formGroupTemplate.toDate).format('YYYY-MM-DD'));
        Vue.set(state.formGroupTemplate, "prevFromDate", moment(formGroupTemplate.fromDate).format('YYYY-MM-DD'));
      }
    },
    setFormGroupTemplateFromDate(state, fromDate) {
      Vue.set(state.formGroupTemplate, "fromDate", fromDate);
    },
    setFormGroupTemplateToDate(state, toDate) {
      Vue.set(state.formGroupTemplate, "toDate", toDate);
    },
    setFormGroupTemplateState(state, itemState) {
      Vue.set(state.formGroupTemplate, "state", itemState);
    },
    setFormGroupTemplateAllocations(state, allocations) {
      Vue.set(state.formGroupTemplate, 'allocations', allocations);
    },
    clearFormGroupTemplate(state) {
      state.formGroupTemplate = null;
    },
    setIsNewGroupTemplate(state, isNew) {
      state.isNewGroupTemplate = isNew;
    },
    sortEmployeeStatistics(state, compareMethod) {
      state.employeeStatistics = state.employeeStatistics.sort(compareMethod);
    },
    setFormGroupTemplateProperty(state, {propertyName, value}) {
      Vue.set(state.formGroupTemplate, propertyName, value);
    },
    setLoadStatisticsProperties(state, {groupFilter, regionFilter, startDate, endDate}) {
      if (groupFilter) state.loadStatisticsGroupFilter = groupFilter;
      if (regionFilter) state.loadStatisticsRegionFilter = regionFilter;
      if (startDate) state.loadStatisticsStartDate = startDate;
      if (endDate) state.loadStatisticsEndDate = endDate;
    }

  },
  actions: {
    initializeStatistics({commit, state, rootState}, {totalDays, groupFilter}) {
      const emp = rootState.employees.employees.filter(o => o.groupId === groupFilter || o.groups.find(f => f.groupId === groupFilter));
      
      let dayGroup = [];
      for (const em of emp) {
        let isGroupReplacement = em.area === 0;
        let isStandIn = false;
        let circleId = em.circleId;
        let weekDayGroupId = em.weekDayGroupId;
        let weekendGroupId = em.weekendGroupId;
        let maxWorkInRow = 99;
        let weekdays = [];
        let orderNumber = em.orderNumber;
        if(em.groupId !== groupFilter) {
          var grp = em.groups.find(o => o.groupId === groupFilter);
          if(grp){
            isGroupReplacement = grp.isGroupReplacement;
            isStandIn = grp.isStandIn;
            circleId = grp.circleId;
            weekdays = grp.weekdays;
            weekendGroupId = grp.weekendGroupId;
            weekDayGroupId = grp.weekDayGroupId;
            orderNumber = grp.orderNumber;
            if(grp.maxWorkInRow && grp.maxWorkInRow > 0) {
              maxWorkInRow = grp.maxWorkInRow;
            }
          }
        }

        commit('pushToEmployeeStatistics', {
          employee: em,
          circleId: circleId,
          baseCircleId: circleId,
          isGroupReplacement: isGroupReplacement,
          isStandIn: isStandIn,
          weekendGroupId: weekendGroupId,
          weekDayGroupId: weekDayGroupId, 
          weekdays: weekdays,
          orderNumber: orderNumber,
          allocations: {
            free: totalDays - rootState.allocations.allocationsTimeOff.filter(o => o.employeeId === em.id).filter(o => moment(o.startDate) < moment(state.formGroupTemplate.toDate)).length,
            saturday: 0,
            sunday: 0,
            vacation: rootState.allocations.allocationsTimeOff.filter(o => o.employeeId === em.id && !o.circleId).filter(o => moment(o.startDate) < moment(state.formGroupTemplate.toDate)).length,
            holiday: 0, // TODO - Add filter and distribution
            work: 0,
            other: 0,
            freeInRow: 0,
            maxWorkInRow: maxWorkInRow,
            currentWorkInRow: 0,
            standIn: 0
          }
        });

        if(!dayGroup.find(o => o === weekDayGroupId)){
          dayGroup.push(weekDayGroupId);
        }
      }

      commit('sortEmployeeStatistics', compareEmployeeOrderNumber);

      return dayGroup;
    },

    finalizeStatistics({commit, state}, totalDays) {      
      for (const em of state.employeeStatistics) {
        em.allocations.free = totalDays - (em.allocations.vacation + em.allocations.holiday + em.allocations.saturday + em.allocations.sunday + em.allocations.work);
      }      
      commit('sortEmployeeStatistics', compareEmployeeOrderNumber);
    },

    async loadStatistics({commit, state, rootState}, {groupFilter, regionFilter, startDate, endDate} = {}) {
      commit('setStatisticsApiState', ApiState.LOADING);
      try {
        commit('setLoadStatisticsProperties', {groupFilter, regionFilter, startDate, endDate});
        commit('clearEmployeeStatistics');

        const {data: employeeStatistics} = await allocationsRepository.getAggregate(state.loadStatisticsStartDate, state.loadStatisticsEndDate, state.loadStatisticsGroupFilter, state.loadStatisticsRegionFilter)
        console.log(employeeStatistics)
        commit('setEmployeeStatistics', employeeStatistics);
        commit('sortEmployeeStatistics', compareEmployeeOrderNumber);

        commit('setStatisticsApiState', ApiState.LOADED);
      }
      catch (error) {
        console.error(error);
        commit('setStatisticsApiState', ApiState.ERROR);
      }
    },

    async setLoadStatisticsStartDate({commit, dispatch}, startDate) {
      commit('setLoadStatisticsProperties', {startDate: moment(startDate) });
      await dispatch('loadStatistics');
    },
    async setLoadStatisticsEndDate({commit, dispatch}, endDate) {
      commit('setLoadStatisticsProperties', {endDate: moment(endDate) });
      await dispatch('loadStatistics');
    },

    async addAllocation({commit, dispatch, state}, {data, groupFilter}) {
      if (!state.formGroupTemplate) {
        alert("Der er ikke valgt nogen template endnu!")
        return;
      }

      if (!state.formGroupTemplate.isTemplateManual) {
        return;
      }
      
      if(moment(data.startDate).toDate() < moment(state.formGroupTemplate.fromDate).toDate()) {
        console.log(moment(data.startDate).format("YYYY-MM-DD") + " < " + moment(state.formGroupTemplate.fromDate).format("YYYY-MM-DD"));
        alert("Dagen er før starts dato for opsætningen og er derfor ikke valid!")
        return;
      }

      if(moment(data.startDate).toDate() > moment(state.formGroupTemplate.toDate).add(1, 'days').toDate()) {
        console.log(moment(data.startDate).format("YYYY-MM-DD") + " > " + moment(state.formGroupTemplate.toDate).format("YYYY-MM-DD"));
        alert("Dagen er efter slut dato for opsætningen og er derfor ikke valid!")
        return;
      }
      if(data.split) {
        for(let i = 0; i < data.allocationSpanInDays; i++) {
          const date = moment.utc(data.startDate).add(i, 'days').toDate()
          const response = await groupTemplateRepository.createGroupTemplateAllocation({
            allocationType: data.allocationType,
            circleId: data.circleId,
            groupId: state.formGroupTemplate.groupId,
            groupTemplateId: state.formGroupTemplate.id,
            employeeId: data.employeeId,
            startDate: date,
            endDateInclusive: date,
            allocationSpanInDays: 1,
            id: generateUUID(),
            comment: data.comment,
          });
    
          commit('pushToAllocations', response.data);
        }
      } else {
        const response = await groupTemplateRepository.createGroupTemplateAllocation({
          allocationType: data.allocationType,
          circleId: data.circleId,
          groupId: state.formGroupTemplate.groupId,
          groupTemplateId: state.formGroupTemplate.id,
          employeeId: data.employeeId,
          startDate: data.startDate,
          endDateInclusive: moment.utc(data.startDate).add(data.allocationSpanInDays - 1, 'days').toDate(),
          allocationSpanInDays: data.allocationSpanInDays,
          id: generateUUID(),
          comment: data.comment,
        });
  
        commit('pushToAllocations', response.data);
      }

      dispatch('updateManuel', {groupFilter}); //this.updateManuel();
    },

    async moveAllocations({commit, dispatch, state}, daysToMove) {
      const groupFilter = state.formGroupTemplate.groupId;
      commit('setStatisticsApiState', ApiState.LOADING);
      try {
        const allocationsToRepeat = state.allocations.filter(o => !o.isRepeated);
        for (let allocation of allocationsToRepeat) {
          allocation.startDate = moment(allocation.startDate).add(daysToMove, 'days').format('YYYY-MM-DD');          
          allocation.endDateInclusive = moment.utc(allocation.startDate).add(allocation.allocationSpanInDays - 1, 'days').format('YYYY-MM-DD');
          const response = await groupTemplateRepository.updateGroupTemplateAllocation(allocation);
          allocation._UpdateTag = response.data._UpdateTag;
        }
        commit('setStatisticsApiState', ApiState.LOADED);        
      }
      catch (error) {
        console.error(error);
        commit('setStatisticsApiState', ApiState.ERROR);
      }
      await dispatch('generateFormGroupTemplate', { groupFilter  });
    },

    filterAllocations({commit}, element) {
      commit('filterAllocations', element);
    },

    updateManuel({commit, state, rootState}, {groupFilter}) {
      console.log("updateManuel");

      if (!state.formGroupTemplate.isTemplateManual) {
        return;
      }

      if(state.formGroupTemplate.repeatWeeks > 0) {
        const allocationsToRepeat = state.allocations.filter(o => !o.isRepeated);
        for (let j = 1; j < state.formGroupTemplate.repeatWeeks + 1; j++) {
          for (let data of allocationsToRepeat) {
            let startDate = moment(data.startDate).add(state.formGroupTemplate.planningWeeks * j, 'weeks').format("YYYY-MM-DD");
            //console.log(startDate);

            let newAllocation = {
              allocationType: data.allocationType,
              circleId: data.circleId,
              groupId: data.groupId,
              groupTemplateId: data.id,
              employeeId: data.employeeId,
              startDate: startDate,
              endDateInclusive: moment.utc(startDate).add(data.allocationSpanInDays - 1, 'days').toDate(),
              allocationSpanInDays: data.allocationSpanInDays,
              id: generateUUID(),
              isRepeated: true
            };

            commit('pushToAllocations', newAllocation);
          }
        }
      }

      commit('setFormGroupTemplateAllocations', state.allocations); //this.formGroupTemplate.allocations = this.allocations;

      const emp = rootState.employees.employees.filter(o => o.groupId === groupFilter || o.groups.find(f => f.groupId === groupFilter));
      const totalDays = state.formGroupTemplate.planningWeeks * 7 * (state.formGroupTemplate.repeatWeeks + 1);

      commit('clearEmployeeStatistics');

      const shiftCircle = rootState.circles.circles.find(o => o.title === "Bagvagt");

      for(const em of emp) {
        let isGroupReplacement = em.area === 0;
        let isStandIn = false;
        let circleId = em.circleId;
        let weekDayGroupId = em.weekDayGroupId;
        let weekendGroupId = em.weekendGroupId;
        let maxWorkInRow = 99;
        let weekdays = [];
        let orderNumber = em.orderNumber;
        if (em.groupId !== groupFilter) {
          var grp = em.groups.find(o => o.groupId === groupFilter);
          if (grp) {
            isGroupReplacement = grp.isGroupReplacement;
            isStandIn = grp.isStandIn;
            circleId = grp.circleId;
            weekdays = grp.weekdays;
            weekendGroupId = grp.weekendGroupId;
            weekDayGroupId = grp.weekDayGroupId;
            orderNumber = grp.orderNumber;
            if (grp.maxWorkInRow && grp.maxWorkInRow > 0) {
              maxWorkInRow = grp.maxWorkInRow;
            }
          }
        }

        let free = totalDays;
        let saturdays = 0;
        let sundays = 0;
        let work = 0;
        let standIn = 0;
        let allocations = state.allocations.filter(o => o.employeeId === em.id);

        for (let allocation of allocations) {
          for (let i = 0; i < allocation.allocationSpanInDays; i++) {
            let date = moment.utc(allocation.startDate).add(i, 'days');
            const wDay = date.isoWeekday();
         
            if(shiftCircle && shiftCircle.id === allocation.circleId){
              standIn += 1;
            } else {
              if(wDay === 6) {
                saturdays += 1;
              }else if(wDay === 7) {
                sundays += 1;
              }else{
                work += 1;
              } 
            }
            console.log('e: ' + em.number + ' ' + moment(date).format('YYYY-MM-DD') + ' w: ' + work + ' sat: ' + saturdays + ' sun: ' + sundays);
          }
          
          free -= allocation.allocationSpanInDays;
        }

        commit('pushToEmployeeStatistics', {
          employee: em,
          circleId: circleId,
          baseCircleId: circleId,
          isGroupReplacement: isGroupReplacement,
          isStandIn: isStandIn,
          weekendGroupId: weekendGroupId,
          weekDayGroupId: weekDayGroupId, 
          weekdays: weekdays,
          orderNumber: orderNumber,
          allocations: {
            free: free,
            saturday: saturdays,
            sunday: sundays,
            vacation: 0,
            holiday: 0,
            work: work,
            other: 0,
            freeInRow: 0,
            maxWorkInRow: maxWorkInRow,
            currentWorkInRow: 0,
            standIn: standIn
          }
        });
      }

      commit('sortEmployeeStatistics', compareOrderNumber);
    },

    /*
     * TODO: Remember to update this.selectedDate in the view with the output-date from this promise.
     */
    async generateFormGroupTemplate({commit, state, rootState, dispatch}, {groupFilter}) { // generateClicked
      if (state.formGroupTemplate) {
        commit('setStatisticsApiState', ApiState.LOADING);

        if (state.formGroupTemplate.isTemplateManual) {
          try {
            // LOAD TEMPLATE ALLOCATIONS
            const response = await groupTemplateRepository.loadGroupTemplateAllocation({id: state.formGroupTemplate.id});
            //console.log(response.data);
            commit('setAllocations', response.data);
            dispatch('updateManuel', {groupFilter}); // this.updateManuel();
            commit('setStatisticsApiState', ApiState.LOADED);
          }
          catch (error) {
            console.error(error);
            commit('setStatisticsApiState', ApiState.ERROR);
          }

          return moment(state.formGroupTemplate.fromDate).utc().startOf('day').toDate()
        }

        try {
          commit('clearAllocations');
          commit('clearEmployeeStatistics');
          let totalDays = 90;
          if (state.formGroupTemplate.toDate === state.formGroupTemplate.fromDate) {
            commit('setFormGroupTemplateToDate', moment.utc(state.formGroupTemplate.fromDate).add(totalDays, 'days').format('YYYY-MM-DD'));
          } else {
            var a = moment(state.formGroupTemplate.fromDate);
            var b = moment(state.formGroupTemplate.toDate);
            totalDays = b.diff(a, 'days') + 1;   // =1
          }
  
          if (state.formGroupTemplate.isTemplateDynamic) {
            for (const item of rootState.allocations.allocationsTimeOff){
              if (moment(item.startDate) < moment(state.formGroupTemplate.toDate)) {
                commit('pushToAllocations', item);
              }          
            }
          }
          console.log("Generating");
          
          const group = state.formGroupTemplate;
          if (!group) {
            return;
          }
  
          if (!group.isSaturdayWeekendGroup) {
            group.isSaturdayWeekendGroup = false;
          }
  
          let circles = rootState.circles.circles.filter(o => o.groupId === groupFilter);
          if(circles.length === 0) {
            circles = rootState.circles.circles;
          }
  
          let date = moment.utc(state.formGroupTemplate.fromDate).toDate();
          let days = 0;
          let dayGroup = await dispatch('initializeStatistics', {totalDays, groupFilter});
  
          // Loop days
          // Loop mini groups
          // Add allocations based on group
          while (days <  totalDays){
            const cDay = moment.utc(date).add(days, 'days');
            const wDay = cDay.isoWeekday();
            var indexDate = moment.utc(cDay);
            let isHoliday = rootState.holidayStore.holidays && rootState.holidayStore.holidays.find(o => indexDate.isSame(moment(o.date), 'date'));
            let employeeDay = [];
  
            if(state.formGroupTemplate.isTemplateDynamic) {
              employeeDay = await dispatch('getEmployeesForDay', {date: cDay, group: group, dayGroup: [...new Set(dayGroup)] });
            } else {
              employeeDay = await dispatch('getSimpleEmployeesForDay', {date: cDay, group: group, dayGroup: [...new Set(dayGroup)] });
            }
  
            for (const e of employeeDay) {
              let circleId =  circles[0].id;
              let circleTitle =  circles[0].title;
  
              if(e.circleId) {
                circleId = e.circleId;
              }
  
              commit('pushToAllocations', {
                adminNote: "",
                allocatedHoursPerDay: 7,
                allocatedTotalHours: 7,
                allocationSpanInDays: 1,
                allocationType: isHoliday 
                  ? "WORKHOLIDAY"
                  : wDay === 6 
                    ? "WORKINGSATURDAY" 
                    : wDay === 7 
                      ? "WORKINGSUNDAY" 
                      : "WORK",
                circleColor: null,
                circleId: circleId,
                circleName: circleTitle,
                comment: "",
                created: new Date(),
                employeeId: e.employee.id,
                employeeName: e.employee.name,
                endDateInclusive: moment.utc(date).add(days + 1, 'days').toDate(),
                id: generateUUID(), // "00000000-0000-0000-0000-000000000000",
                note: "",
                number: e.employee.number,
                region: e.employee.region,
                replacementId: null,
                replacementName: null,
                startDate: cDay.toDate(),
                isSimulation: true
              });
            }
            
            days = days + 1;
          }
          
          dispatch('finalizeStatistics', {totalDays, groupFilter});
  
          commit('setFormGroupTemplateAllocations', state.allocations.filter(o => o.isSimulation));
  
          commit('setStatisticsApiState', ApiState.LOADED);
          return moment(state.formGroupTemplate.fromDate).utc().startOf('day').toDate();

        }
        catch (error) {
          console.error(error);
          commit('setStatisticsApiState', ApiState.ERROR);
        }
      }
      return null;
    },

    getEmployeesForDay({commit, state, rootState}, {date, group, dayGroup}) { 
      const compareDayGroupSaturDay = (a, b) => {
        let ae = state.employeeStatistics.filter(o => o.weekDayGroupId === a);
        let be = state.employeeStatistics.filter(o => o.weekDayGroupId === b);
        let as = 0;
        let bs = 0;
        for(let i of ae) {
          if(i.allocations.saturday > as) {
            as = i.allocations.saturday;
          }
        }
        for(let i of be) {
          if(i.allocations.saturday > bs) {
            bs = i.allocations.saturday;
          }
        }
        if(as < bs) {
          return -1;
        }
        if(as > bs) {
          return 1;
        }
        return 0;
      };

      const compareDayGroupSunday= (a, b) => {
        let ae = state.employeeStatistics.filter(o => o.weekDayGroupId === a);
        let be = state.employeeStatistics.filter(o => o.weekDayGroupId === b);
        let as = 0;
        let bs = 0;
        for(let i of ae) {
          if(i.allocations.sunday > as) {
            as = i.allocations.sunday;
          }
        }
        for(let i of be) {
          if(i.allocations.sunday > bs) {
            bs = i.allocations.sunday;
          }
        }
        if(as < bs) {
          return -1;
        }
        if(as > bs) {
          return 1;
        }
        return 0;
      };

      const lookUp = moment.utc(date).format('YYYY-MM-DD');
      const day = date.isoWeekday();
      var indexDate = moment.utc(date);
      let isHoliday = rootState.holidayStore.holidays && rootState.holidayStore.holidays.find(o => indexDate.isSame(moment(o.date), 'date'));
      let freeInRow = 1;

      if(group.daysFreeInRow && group.daysFreeInRow > 0){
        freeInRow = group.daysFreeInRow;
      }

      // 1: Check work
      // 2: Check holiday
      // 3: Check free in row
      const compareWeekDay = (a, b) => {
        let ad = a.allocations.work;
        let bd = b.allocations.work;
        let af = a.allocations.freeInRow;
        let bf = b.allocations.freeInRow;
        let maxWorkInRow = a.allocations.maxWorkInRow;

        if(maxWorkInRow < 99 && a.allocations.currentWorkInRow > 2) {
          console.log('w-maxWorkInRow: ' + maxWorkInRow);
          return 1;
        }
        if(group.isSaturdayWeekendGroup === false) {
          ad = a.allocations.saturday + a.allocations.work;
          bd = b.allocations.saturday + b.allocations.work;
        }
        if(isHoliday) {
          ad = a.allocations.holiday;
          bd = b.allocations.holiday;
        }
        if(ad === bd) {
          ad = a.orderNumber;
          bd = b.orderNumber;
        }
        return compare(ad, bd, af, bf, freeInRow);
      };

      const compareSaturday = (a, b) => {
        let ad = a.allocations.saturday;        
        let bd = b.allocations.saturday;
        let af = a.allocations.freeInRow;
        let bf = b.allocations.freeInRow;
        let maxWorkInRow = a.allocations.maxWorkInRow;
      
        if(maxWorkInRow < 99 && a.allocations.currentWorkInRow > 2) {
          console.log('sa-maxWorkInRow: ' + maxWorkInRow);
          return 1;
        }
        if(group.isSaturdayWeekendGroup === false) {
          // TODO: Check all days
          if(isActive(2, a.weekdays) && isActive(2, b.weekdays)){
            ad = a.allocations.saturday + a.allocations.work;
            bd = b.allocations.saturday + b.allocations.work;
          }
        }
        if(isHoliday) {
          ad = a.allocations.holiday;
          bd = b.allocations.holiday;
        }
        if(ad === bd) {
          ad = a.orderNumber;
          bd = b.orderNumber;
        }
        return compare(ad, bd, af, bf, freeInRow);
      };

      const compareSunday = (a, b) => {
        let ad = a.allocations.sunday;
        let bd = b.allocations.sunday;
        let af = a.allocations.freeInRow;
        let bf = b.allocations.freeInRow;
        let maxWorkInRow = a.allocations.maxWorkInRow;
      
        if(maxWorkInRow < 99 && a.allocations.currentWorkInRow > 2) {
          console.log('su-maxWorkInRow: ' + maxWorkInRow);
          return 1;
        }
        if(isHoliday) {
          ad = a.allocations.holiday;
          bd = b.allocations.holiday;
        }
        if(ad === bd) {
          ad = a.orderNumber;
          bd = b.orderNumber;
        }
        return compare(ad, bd, af, bf, freeInRow);
      };

      var compareStandIn = (a, b) => {
        let ad = a.allocations.standIn;
        let bd = b.allocations.standIn;
        let af = 0;
        let bf = 0;

        if(ad === bd) {
          ad = a.orderNumber;
          bd = b.orderNumber;
        }
        
        return compare(ad, bd, af, bf, freeInRow);
      };
      
      const days = [1,2,3,4,5];      
      let result = [];

      // Saturday
      // TODO: where not hole numers are used
      if(day === 6 && group.isSaturdayWeekendGroup) {
        const saturday = group.atWorkWeekSaturday;
        const calc = saturday / dayGroup.length;
        if (!days.find(o => o == calc)) {
          let added = saturday;
          let employees = state.employeeStatistics.sort(compareSaturday);
          
          for(let dg of dayGroup) {
            for(let e of employees) {
              if(e.weekDayGroupId === dg) {
                if(allocationForDay(lookUp, e.employee.id, rootState.allocations.allocationsTimeOff).length === 0 && isActive(day, e.weekdays)) {
                  if(added > 0) {
                    result.push(e);
                    e.allocations.saturday = e.allocations.saturday + 1;
                    e.allocations.free = e.allocations.free - 1;
                    e.allocations.freeInRow = 0;
                    e.allocations.currentWorkInRow += 1;
                  }
                  added = added - 1;
                  break;
                }

                if(isActive(day, e.weekdays) === false){
                  e.allocations.freeInRow = 0;
                }
              }
            }
          }

          for(let e of employees) {
            if(!result.find(o => o.employee.id === e.employee.id)) {
              if(allocationForDay(lookUp, e.employee.id, rootState.allocations.allocationsTimeOff).length === 0 && isActive(day, e.weekdays)) {
                if(added > 0) {
                  // TODO
                  result.push(e);
                  e.allocations.saturday = e.allocations.saturday + 1;
                  e.allocations.free = e.allocations.free - 1;
                  e.allocations.freeInRow = 0;
                  e.allocations.currentWorkInRow += 1;
                } else {
                  if(e.allocations.freeInRow === 0 || e.allocations.freeInRow % freeInRow !== 0) {
                    e.allocations.freeInRow = e.allocations.freeInRow + 1;
                  }
                  e.allocations.currentWorkInRow = 0;
                }
                added = added - 1;
              }
              if(isActive(day, e.weekdays) === false){
                e.allocations.freeInRow = 0;
              }
            }
          }
          return result;
        }
      }

      // Sunday
      // TODO: where not whole numers are used
      if(day === 7) {
        const sunday = group.atWorkWeekSunday;
        const calc = sunday / dayGroup.length;
        if (!days.find(o => o == calc)) {          
          let added = sunday;
          let employees = state.employeeStatistics.sort(compareSunday);
          
          for(let dg of dayGroup) {
            for(let e of employees) {
              if(e.weekDayGroupId === dg) {
                if(allocationForDay(lookUp, e.employee.id, rootState.allocations.allocationsTimeOff).length === 0 && isActive(day, e.weekdays)) {
                  if(added > 0) {                    
                    result.push(e);
                    e.allocations.sunday = e.allocations.sunday + 1;
                    e.allocations.free = e.allocations.free - 1;
                    e.allocations.freeInRow = 0;
                    e.allocations.currentWorkInRow += 1;
                  }
                  added = added - 1;
                  break;
                }
                if(isActive(day, e.weekdays) === false){
                  e.allocations.freeInRow = 0;
                }
              }
            }
          }

          for(let e of employees) {
            if(!result.find(o => o.employee.id === e.employee.id)) {
              if(allocationForDay(lookUp, e.employee.id, rootState.allocations.allocationsTimeOff).length === 0 && isActive(day, e.weekdays)) {
                if(added > 0) {
                  // TODO
                  result.push(e);
                  e.allocations.sunday = e.allocations.sunday + 1;
                  e.allocations.free = e.allocations.free - 1;
                  e.allocations.freeInRow = 0;
                  e.allocations.currentWorkInRow += 1;
                } else {
                  if(e.allocations.freeInRow === 0 || e.allocations.freeInRow % freeInRow !== 0) {
                    e.allocations.freeInRow = e.allocations.freeInRow + 1;
                  }
                  e.allocations.currentWorkInRow = 0;
                }
                added = added - 1;
              }
              if(isActive(day, e.weekdays) === false){
                e.allocations.freeInRow = 0;
              }
            }
          }
          return result;
        }
      }

      let atWork = group.atWorkWeekDays;
      if (day === 6) {
        atWork = group.atWorkWeekSaturday;
      } else if(day === 7) {
        atWork = group.atWorkWeekSunday;
      } 

      let weekDay = atWork / dayGroup.length;
            
      let sortedDayGroup = dayGroup;
      // If value is not hole number
      if (!days.find(o => o == weekDay)) {
        if(day === 6) {
          sortedDayGroup = dayGroup.sort(compareDayGroupSaturDay);
        } else if(day === 7) {
          //sortedDayGroup = dayGroup.sort(this.compareDayGroup);
        } 
      }

      console.log("dayGroup");

      for(let dg of sortedDayGroup) {
        let added = weekDay;

        let employees = state.employeeStatistics.filter(o => o.weekDayGroupId === dg).sort(compareWeekDay);

        if(employees.filter(o => o.isGroupReplacement === false).length === 1){
          added = 1;
        }

        if (day === 6) {
          employees = employees.sort(compareSaturday);
        } else if (day === 7) {
          employees = employees.sort(compareSunday);
        } 
        let grpResult = [];
        let circleIds = [];
        for(let circle of employees.filter(o => o.isGroupReplacement === false)) {
          circleIds.push(circle.baseCircleId);
          if(isAllocatedForDay(lookUp, circle.employee.id, rootState.allocations.allocationsTimeOff).length > 0) {
            circle.allocations.work = circle.allocations.work + 1; // Work allocated
            circle.allocations.currentWorkInRow += 1;
            added = added - 1;
            atWork -= 1;
          }
        }
        for(let e of employees) {          
          if(allocationForDay(lookUp, e.employee.id, rootState.allocations.allocationsTimeOff).length === 0 && isActive(day, e.weekdays)) {
            if(added > 0 && atWork > 0) {
              
              // Group replacement
              if(e.isGroupReplacement) {
                if(circleIds.length > 0) {
                  e.circleId = circleIds[0];
                } else {
                  e.circleId = e.baseCircleId;
                }
              } else {
                e.circleId = e.baseCircleId;
              }

              circleIds = circleIds.filter(o => o !== e.circleId);
              
              result.push(e);

              let workLoad = 1;

              if (e.employee.workLoad > 0 && e.employee.workLoad < 100) {
                workLoad = 1 + (e.employee.workLoad / 100);
              }

              if (day === 6) {
                e.allocations.saturday = e.allocations.saturday + 1 * workLoad;
              } else if(day === 7) {
                e.allocations.sunday = e.allocations.sunday + 1 * workLoad;
              } else {
                if(isHoliday) {
                  e.allocations.holiday = e.allocations.holiday + 1 * workLoad;
                } else {
                  e.allocations.work = e.allocations.work + 1 * workLoad;
                }
              }
              
              e.allocations.freeInRow = 0;
              e.allocations.currentWorkInRow += 1;
              atWork -= 1;
            } else {
              if(e.allocations.freeInRow === 0 || e.allocations.freeInRow % freeInRow !== 0) {
                e.allocations.freeInRow = e.allocations.freeInRow + 1;
              }
              if(e.allocations.maxWorkInRow < 99) {
                //debugger;
              }

              e.allocations.currentWorkInRow = 0;
            }
            added = added - 1;
          }
          if(isActive(day, e.weekdays) === false){
            e.allocations.freeInRow = 0;
          }
        }
        result.push(...grpResult);
      }
      
      if(day === 7) {        
        console.log(state.employeeStatistics);
        let employeeStandIns = state.employeeStatistics.filter(o => o.isStandIn).sort(compareStandIn);
        for (let se of employeeStandIns) {
          let circleStandIn = rootState.circles.circles.find(o => o.title === "Bagvagt");

          if (!circleStandIn) {
            circleStandIn = rootState.circles.circles[0];
          }
          
          if (result.filter(o => o.employee.id === se.employee.id).length === 0) {
            se.circleId = circleStandIn.id;
            se.allocations.standIn += 1;
            result.push(se);          
            break;
          }
        }
      }

      return result;
    },

    getSimpleEmployeesForDay({state, rootState}, {date, group, dayGroup}) {
      let freeInRow = 1;
      const day = date.isoWeekday();
      const lookUp = moment.utc(date).format('YYYY-MM-DD');

      if(group.daysFreeInRow && group.daysFreeInRow > 0){
        freeInRow = group.daysFreeInRow;
      }
      var compare = (a, b) => {
        let ad = a.allocations.work + a.allocations.saturday  + a.allocations.sunday;
        let bd = b.allocations.work + b.allocations.saturday  + b.allocations.sunday;
        let af = a.allocations.freeInRow;
        let bf = b.allocations.freeInRow;
        
        if(day === 7) {         
          console.log(`${day} (${lookUp}): a (${ad}-${af}-${a.allocations.dayFree}), b (${bd}-${bf}-${b.allocations.dayFree})`);
        }

        if(freeInRow === 3){
          if(a.allocations.dayFree && a.allocations.dayFree === 7) {
            if(day === 3) {
              return 1;
            }
          }
          if(a.allocations.dayFree && a.allocations.dayFree === 4) {
            if(day === 7) {
              return 1;
            }
          }
          if(b.allocations.dayFree && b.allocations.dayFree === 7) {            
            if(day === 3) {
              return -1;
            }
          }
          if(b.allocations.dayFree && b.allocations.dayFree === 4) {
            if(day === 7) {
              return -1;
            }
          }
        }

        if(af === freeInRow) {
          af = 0;
        }

        if(bf === freeInRow) {
          bf = 0;
        }
        
        if(af < bf){
          return -1;
        }

        if(af > bf){
          return 1;
        }

        if(ad === bd) {
          ad = a.orderNumber;
          bd = b.orderNumber;
        }

        if(ad < bd) {
          return -1;
        }

        if(ad > bd) {
          return 1;
        }

        return 0;
      };

      let result = [];
      var indexDate = moment.utc(date);
      let isHoliday = rootState.holidayStore.holidays && rootState.holidayStore.holidays.find(o => indexDate.isSame(moment(o.date), 'date'));
      let atWork = group.atWorkWeekDays;
      if (day === 6) {
        atWork = group.atWorkWeekSaturday;
      } else if(day === 7) {
        atWork = group.atWorkWeekSunday;
      } 
      
      let employees = state.employeeStatistics;
      let circleIds = [];
      for (let circle of employees.filter(o => o.isGroupReplacement === false)) {
        circleIds.push(circle.baseCircleId);
      }

      if(group.daysFreeInRow === 4) {
        if (day === 4) {
          for(let e of employees) {
            e.allocations.freeInRow = 0;
          }
        }

        employees = state.employeeStatistics.sort(compare);
        
        for(let e of employees) {
          if(atWork > 0) {            
            // Group replacement
            if(e.isGroupReplacement) {
              if(circleIds.length > 0) {
                e.circleId = circleIds[0];
              } else {
                e.circleId = e.baseCircleId;
              }
            } else {
              e.circleId = e.baseCircleId;
            }

            circleIds = circleIds.filter(o => o !== e.circleId);
            
            result.push(e);

            let workLoad = 1;

            if (day === 6) {
              e.allocations.saturday = e.allocations.saturday + 1 * workLoad;
            } else if(day === 7) {
              e.allocations.sunday = e.allocations.sunday + 1 * workLoad;
            } else {
              if(isHoliday) {
                e.allocations.holiday = e.allocations.holiday + 1 * workLoad;
              } else {
                e.allocations.work = e.allocations.work + 1 * workLoad;
              }
            }
            
            e.allocations.freeInRow = 0;
            e.allocations.currentWorkInRow += 1;
            atWork -= 1;
          } else {
            e.allocations.freeInRow += 1;
          }          
        }
      }
      
      if(group.daysFreeInRow === 3) {
        if (day === 3 || day === 5) {
          for(let e of employees) {
            e.allocations.freeInRow = 0;
          }
        }
        
        employees = state.employeeStatistics.sort(compare);
        
        for(let e of employees) {
          if(atWork > 0) {            
            // Group replacement
            if(e.isGroupReplacement) {
              if(circleIds.length > 0) {
                e.circleId = circleIds[0];
              } else {
                e.circleId = e.baseCircleId;
              }
            } else {
              e.circleId = e.baseCircleId;
            }

            circleIds = circleIds.filter(o => o !== e.circleId);
            
            result.push(e);

            let workLoad = 1;

            if (day === 6) {
              e.allocations.saturday = e.allocations.saturday + 1 * workLoad;
            } else if(day === 7) {
              e.allocations.sunday = e.allocations.sunday + 1 * workLoad;
            } else {
              if(isHoliday) {
                e.allocations.holiday = e.allocations.holiday + 1 * workLoad;
              } else {
                e.allocations.work = e.allocations.work + 1 * workLoad;
              }
            }
            
            e.allocations.freeInRow = 0;
            e.allocations.currentWorkInRow += 1;
            atWork -= 1;
          } else {
            e.allocations.freeInRow += 1;
            e.allocations.dayFree = day;
          }          
        }
      }
      if(group.daysFreeInRow === 2) {
        if (day === 2 || day === 4  || day === 6) {
          for(let e of employees) {
            e.allocations.freeInRow = 0;
          }
        }
        
        employees = state.employeeStatistics.sort(compare);
        
        for(let e of employees) {
          if(atWork > 0) {            
            // Group replacement
            if(e.isGroupReplacement) {
              if(circleIds.length > 0) {
                e.circleId = circleIds[0];
              } else {
                e.circleId = e.baseCircleId;
              }
            } else {
              e.circleId = e.baseCircleId;
            }

            circleIds = circleIds.filter(o => o !== e.circleId);
            
            result.push(e);

            let workLoad = 1;

            if (day === 6) {
              e.allocations.saturday = e.allocations.saturday + 1 * workLoad;
            } else if(day === 7) {
              e.allocations.sunday = e.allocations.sunday + 1 * workLoad;
            } else {
              if(isHoliday) {
                e.allocations.holiday = e.allocations.holiday + 1 * workLoad;
              } else {
                e.allocations.work = e.allocations.work + 1 * workLoad;
              }
            }
            
            e.allocations.freeInRow = 0;
            e.allocations.currentWorkInRow += 1;
            atWork -= 1;
          } else {
            e.allocations.freeInRow += 1;
            e.allocations.dayFree = day;
          }          
        }
      }
      return result;
    },

    initAllocation({commit, state, rootState}, {groupFilter}) {
      console.log("initAllocation");
      if (groupFilter && state.allocations.length === 0) {             
        try {
          commit('setStatisticsApiState', ApiState.LOADING);
          
          for(const item of rootState.allocations.allocationsTimeOff){
            commit('pushToAllocations', item);
          }
          
          const totalDays = 90;
          const emp = rootState.employees.employees.filter(o => o.groupId === groupFilter);
          
          for(const em of emp) {
            commit('pushToEmployeeStatistics', {
              employee: em,
              circleId: null,
              allocations: {
                free: totalDays - rootState.allocations.allocationsTimeOff.filter(o => o.employeeId === em.id).length,
                saturday: 0,
                sunday: 0,
                vacation: rootState.allocations.allocationsTimeOff.filter(o => o.employeeId === em.id && !o.circleId).length,
                holiday: 0, // TODO - Add filter and distribution
                work: rootState.allocations.allocationsTimeOff.filter(o => o.employeeId === em.id && o.circleId && o.replacementId).length,
                other: 0
              }
            });
          }
  
          commit('sortEmployeeStatistics', compareEmployeeOrderNumber);
          commit('setStatisticsApiState', ApiState.LOADED);
        }
        catch (error) {
          console.error(error);
          commit('setStatisticsApiState', ApiState.ERROR);
        }
      } else {        
        commit('setStatisticsApiState', ApiState.LOADED);
      }
    },    

    async setFormGroupTemplate({commit}, formGroupTemplate) {
      commit('setFormGroupTemplate', formGroupTemplate);
    },

    closeGroupTemplateSidebars({commit}) {
      commit('setActiveSidebar', '');
    },

    showGroupTemplateSelectionSidebar({commit}) {
      commit('setActiveSidebar', 'group-templates');
    },

    showGroupTemplateEditSidebar({commit}) {
      commit('setActiveSidebar', 'edit-group-template');
    },

    async copyGroupTemplate({commit, dispatch, rootState, state}, {groupTemplate, groupFilter}){      
      if(groupTemplate) {
        commit('setFormGroupTemplate', groupTemplate);

        var a = moment(state.formGroupTemplate.fromDate);
        var b = moment(state.formGroupTemplate.toDate);
        const totalDays = b.diff(a, 'days')   // =1

        if(state.formGroupTemplate.toDate !== state.formGroupTemplate.fromDate) {
          const newFrom = moment.utc(state.formGroupTemplate.toDate).add(1, 'days');
          const newTo = moment.utc(newFrom).add(totalDays, 'days');
          const newTotalDays = newTo.diff(newFrom, 'days')   // =1

          if(totalDays != newTotalDays) {
            console.log('error: days between not korrekt' + totalDays + ' != ' + newTotalDays);
          }

          commit('setFormGroupTemplateFromDate', moment.utc(newFrom).format('YYYY-MM-DD'));
          commit('setFormGroupTemplateToDate', moment.utc(newTo).format('YYYY-MM-DD'));
        }

        commit('setFormGroupTemplateState', 0);
        const groupTemplateSourceId = state.formGroupTemplate.id;
        
        await dispatch('groupTemplateStore/createGroupTemplate', state.formGroupTemplate, { root: true });
        await dispatch('groupTemplateStore/getAllTemplatesForGroup', groupFilter, { root: true });

        commit('setFormGroupTemplate', rootState.groupTemplateStore.groupTemplate);
        
        const responseCopy = await groupTemplateRepository.copyGroupTemplate({
          groupTemplateSourceId: groupTemplateSourceId,
          groupTemplateDestinationId: state.formGroupTemplate.id
        });

        if (state.formGroupTemplate) {
          commit('setFormGroupTemplateFromDate', moment(state.formGroupTemplate.fromDate).format('YYYY-MM-DD'));
          commit('setFormGroupTemplateToDate', moment(state.formGroupTemplate.toDate).format('YYYY-MM-DD'));
        }
        commit('setIsNewGroupTemplate', false);
      }
    },

    async generateGroupTemplate({commit, dispatch}, {groupTemplate, groupFilter}) {
      commit('setStatisticsApiState', ApiState.LOADING);
      await new Promise(q => setTimeout(q, 500)); // Kind of a hack, to give the UI time to update
      commit('setFormGroupTemplate', groupTemplate);
      commit('setIsNewGroupTemplate', false);
      commit('setActiveSidebar', '');
      return await dispatch('generateFormGroupTemplate', {groupFilter});
    },

    async editGroupTemplate({commit, dispatch}, groupTemplate) {
      commit('setFormGroupTemplate', groupTemplate);
      commit('setIsNewGroupTemplate', false);
      if(groupTemplate) {
        const groupFilter = groupTemplate.groupId;
        await dispatch('generateFormGroupTemplate', { groupFilter  });
      }
      commit('setActiveSidebar', 'edit-group-template');
    },

    async initGroupTemplate({commit, dispatch, rootState, state}, {selectedGroup, fromDate, toDate, groupFilter}) {
      if(selectedGroup) {

        commit('setIsNewGroupTemplate', true);
        
        commit('setFormGroupTemplate', {
          atWorkWeekDays: selectedGroup.atWorkWeekDays,
          atWorkWeekSaturday: selectedGroup.atWorkWeekSaturday,
          atWorkWeekSunday: selectedGroup.atWorkWeekSunday,
          atWorkHolidayWeekdays: selectedGroup.atWorkHolidayWeekdays,
          atWorkHolidayWeekend: selectedGroup.atWorkHolidayWeekend,
          daysFreeInRow: !selectedGroup.daysFreeInRow || selectedGroup.daysFreeInRow <= 0 
            ? 2 
            : selectedGroup.daysFreeInRow,
          isSaturdayWeekendGroup: !selectedGroup.isSaturdayWeekendGroup 
            ? false 
            : selectedGroup.isSaturdayWeekendGroup,
          title: selectedGroup.title,
          fromDate: fromDate,
          toDate: toDate,
          groupId: selectedGroup.id,
          isTemplateManual: true,
          isTemplateDynamic: false,
          planningWeeks: 1,
          repeatWeeks: 0
        });

        if (state.formGroupTemplate.isTemplateManual) {
          const totalDays = (state.formGroupTemplate.planningWeeks * 7 * (state.formGroupTemplate.repeatWeeks + 1)) - 1;
          commit('setFormGroupTemplateToDate', moment(state.formGroupTemplate.fromDate).add(totalDays, 'days').format('YYYY-MM-DD'));
        }

        //this.isSaturdayWeekendGroup = this.formGroupTemplate.isSaturdayWeekendGroup; // not sure that this does anything
         
        await dispatch('allocations/getTimeOff', {fromDate, forGroup: groupFilter}, { root: true });
        await dispatch('groupTemplateStore/getAllTemplatesForGroup', groupFilter, { root: true });
        await dispatch('groupComments/loadGroupComments', {fromDate, toDate, forGroup: groupFilter}, { root: true });
      }      
    },

    async createGroupTemplateFromForm({commit, dispatch, state, rootState}, {groupFilter}) {
      if (!state.formGroupTemplate) {
        return;
      }
      try {
        commit('setStatisticsApiState', ApiState.LOADING);
  
        await dispatch('groupTemplateStore/createGroupTemplate', state.formGroupTemplate, { root: true });
        await dispatch('groupTemplateStore/getAllTemplatesForGroup', groupFilter, { root: true });
  
        commit('setFormGroupTemplate', rootState.groupTemplateStore.groupTemplate);
        commit('setIsNewGroupTemplate', false);
        commit('setStatisticsApiState', ApiState.LOADED);
      }
      catch (error) {
        console.error(error);
        commit('setStatisticsApiState', ApiState.ERROR);
      }
    },

    async updateGroupTemplateFromForm({commit, dispatch, state, rootState}, {fromDate, groupFilter}) { // updateClicked
      console.log(`updateGroupTemplateFromForm({fromDate: ${fromDate}, groupFilter: ${groupFilter}})`);
      if (!state.formGroupTemplate) {
        return;
      }

      const prevFromDate = state.formGroupTemplate.prevFromDate;

      if (state.formGroupTemplate.isTemplateManual) {
        let rw = new Number(state.formGroupTemplate.repeatWeeks);
        let pw = new Number(state.formGroupTemplate.planningWeeks);
        const weeks = pw * (rw + 1);        
        const totalDays = (weeks * 7) - 1;

        commit('setFormGroupTemplateToDate', moment(state.formGroupTemplate.fromDate).add(totalDays, 'days').format('YYYY-MM-DD'));
      }

      await dispatch('groupTemplateStore/updateGroupTemplate', state.formGroupTemplate, { root: true });
      commit('setFormGroupTemplate', rootState.groupTemplateStore.groupTemplate);

      if (fromDate) {
        let spanInDays = dayBetween(fromDate, state.formGroupTemplate.fromDate);

        if(prevFromDate) {
          spanInDays = dayBetween(prevFromDate, state.formGroupTemplate.fromDate);
          console.log('prevFromDate: ' + prevFromDate);
        }

        console.log(spanInDays);
        // TODO: Move existing allocations
        console.log('fromDate: ' + fromDate);
        console.log('newFromDate: ' + state.formGroupTemplate.fromDate);
        if(spanInDays !== 0) {
          dispatch('moveAllocations', spanInDays);
        }
      }

      if (state.formGroupTemplate) {
        commit('setFormGroupTemplateFromDate', moment(state.formGroupTemplate.fromDate).format('YYYY-MM-DD'));
        commit('setFormGroupTemplateToDate', moment(state.formGroupTemplate.toDate).format('YYYY-MM-DD'));

        await dispatch('allocations/getTimeOff', {fromDate: state.formGroupTemplate.fromDate, forGroup: groupFilter}, { root: true });
      }      
    },

    templatePropertyChanged({commit}, {propertyName, value}) {
      commit('setFormGroupTemplateProperty', {propertyName, value});
    },

    async updateGroupTemplate({commit, dispatch}, {fromDate, groupFilter}) {
      commit('setStatisticsApiState', ApiState.LOADING);
      try {
        // await this.updateClicked();
        await dispatch('updateGroupTemplateFromForm', {fromDate, groupFilter});
  
        //this.generateClicked();
        const selectedDate = await dispatch('generateFormGroupTemplate', {groupFilter});
  
        await dispatch('groupTemplateStore/getAllTemplatesForGroup', groupFilter, { root: true });
        
        dispatch('closeGroupTemplateSidebars');
        commit('setStatisticsApiState', ApiState.LOADED);
  
        return selectedDate;
      }
      catch (error) {
        console.error(error);
        commit('setStatisticsApiState', ApiState.ERROR);
      }
    },

    async applyGroupTemplate({commit, dispatch, state}, groupFilter) {
      const response = await groupTemplateRepository.canApply(state.formGroupTemplate);
            
      if(response.data > 0) { 
        const vueTmp = Vue;
        const buefyTmp = vueTmp.prototype.$buefy;
        buefyTmp.dialog.confirm({
          title: "Advarsel",
          message: `Der er ${response.data} allokerede opgaver. Vil du erstatte din nuværende kalender med den nye kalender?`,
          cancelText: 'Nej',
          confirmText: 'Ja',
          type: 'is-success',
          onConfirm: async () => {
            commit('setStatisticsApiState', ApiState.LOADING);
            try {
              dispatch('closeGroupTemplateSidebars');
              console.log(state.formGroupTemplate)
              if(!state.formGroupTemplate.isTemplateManual) {
                commit('setFormGroupTemplateProperty', { propertyName: 'allocations', value: state.allocations.filter(o => o.isSimulation) });
              }
                      
              await dispatch('groupTemplateStore/applyGroupTemplate', state.formGroupTemplate, { root: true });        
              await dispatch('groupTemplateStore/getAllTemplatesForGroup', groupFilter, { root: true });

              commit('clearFormGroupTemplate');
              commit('setStatisticsApiState', ApiState.LOADED);
            }
            catch (error) {
              console.error(error);
              commit('setStatisticsApiState', ApiState.ERROR);
            }
          },
          onCancel:() => {
            dispatch('closeGroupTemplateSidebars');
          }
        });
      }
      else {
        commit('setStatisticsApiState', ApiState.LOADING);
        try {
          dispatch('closeGroupTemplateSidebars');

          if(!state.formGroupTemplate.isTemplateManual) {
            commit('setFormGroupTemplateProperty', { propertyName: 'allocations', value: state.allocations.filter(o => o.isSimulation) });
          }
    
          await dispatch('groupTemplateStore/applyGroupTemplate', state.formGroupTemplate, { root: true });
          await dispatch('groupTemplateStore/getAllTemplatesForGroup', groupFilter, { root: true });

          commit('clearFormGroupTemplate');
          commit('setStatisticsApiState', ApiState.LOADED);
        }
        catch (error) {
          console.error(error);
          commit('setStatisticsApiState', ApiState.ERROR);
        }
      }
    },
    async rollbackGroupTemplate({commit, dispatch, state}, {groupTemplate, groupFilter}) {
      const vueTmp = Vue;
      const buefyTmp = vueTmp.prototype.$buefy;
      commit('setFormGroupTemplate', groupTemplate);
      buefyTmp.dialog.confirm({
        title: "Advarsel",
        message: `Vil du rulle dette template tilbage?`,
        cancelText: 'Nej',
        confirmText: 'Ja',
        type: 'is-success',
        onConfirm: async () => {
          commit('setStatisticsApiState', ApiState.LOADING);
          try {
            dispatch('closeGroupTemplateSidebars');
            console.log(state.formGroupTemplate)
            if(!state.formGroupTemplate.isTemplateManual) {
              commit('setFormGroupTemplateProperty', { propertyName: 'allocations', value: state.allocations.filter(o => o.isSimulation) });
            }
                    
            await dispatch('groupTemplateStore/rollbackGroupTemplate', state.formGroupTemplate, { root: true });        
            await dispatch('groupTemplateStore/getAllTemplatesForGroup', groupFilter, { root: true });
  
            commit('clearFormGroupTemplate');
            commit('setStatisticsApiState', ApiState.LOADED);
          }
          catch (error) {
            console.error(error);
            commit('setStatisticsApiState', ApiState.ERROR);
          }
        },
        onCancel:() => {
          dispatch('closeGroupTemplateSidebars');
        }
      });
    }
  },
  getters: {
    statisticsApiState: state => state.apiState,
    allocations: state => state.allocations,
    employeeStatistics: state => state.employeeStatistics,
    formGroupTemplate: state => state.formGroupTemplate,
    activeSidebar: state => state.activeSidebar,
    isNewGroupTemplate: state => state.isNewGroupTemplate,
    latestUpdateTextFormGroupTemplate: state => {
      if (state.formGroupTemplate) {
        const duration = moment().diff(moment(state.formGroupTemplate._LatestUpdate), "milliseconds");
        const durationText = duration > 0 ? moment.duration(duration).humanize() : '';
  
        return `Opdateret ${durationText} siden af ${state.formGroupTemplate?._UpdatedByName}`;
      }
      return '';
    },
    loadStatisticsStartDate: state => state.loadStatisticsStartDate.toDate(),
    loadStatisticsEndDate: state => state.loadStatisticsEndDate.toDate(),
    loadStatisticsGroupFilter: state => state.loadStatisticsGroupFilter,
    loadStatisticsRegionFilter: state => state.loadStatisticsRegionFilter

  }
}
